function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// Focused Setup checklist — the guided onboarding destination (top-tier design,
// mockups/commerce-onboarding-2026-09-17.html "Checklist"). Renders the ordered
// core from onboarding-status.js, so a Shopify tenant's commerce steps collapse
// into one "Connect your Shopify store" step automatically. /Data/Connect stays
// the advanced feed manager. State DERIVED via onboarding-status.js.

import React from "react";
import { withRouter } from "react-router";
import PubSub from "pubsub-js";
import account from "../../data/account";
import Auth from "../../functions/authenticate";
import { Loader } from "../../components/loaders";
import { getLivePlans } from "../../functions/billing-status";
import { markSetupComplete, getOnboardingStatus, shopifyThemeEmbedUrl } from "../../functions/onboarding-status";
import { hasTenantFeature, adBillingMode, getTenantFeatures, sellsThroughReps } from "../../functions/tenant-features";
import { loadBrandKit } from "../../functions/brand-kit";
import { api as emailApi } from "../email-campaigns/api";
import { OB_CSS } from "./onboarding-css";
const askPlunk = starter => PubSub.publish("plunk-assistant-toggle", {
  starter
});

// Step metadata keyed by step id. onboarding-status decides WHICH keys, and in
// what order, appear (Shopify tenants get `shopify` in place of products/customers/sales).
const META = {
  company: {
    title: "Your company & website",
    label: "Company & website",
    line: "The address powers the tracker check, site monitoring and AI visibility.",
    why: "Everything downstream keys off your site, so confirm it first.",
    nextCopy: "confirm your company & website",
    route: "/Settings",
    cta: "Confirm",
    doneCta: "Edit"
  },
  tracker: {
    title: "Install & verify the tracker",
    label: "Tracker installed & live",
    line: "One script on every page turns on live traffic, contacts and funnels, from today forward.",
    why: "This is the engine: install it and analytics start flowing immediately.",
    nextCopy: "install & verify the tracker, and analytics start today",
    route: "/Tracking",
    cta: "Set up tracking",
    doneCta: "View tracking"
  },
  ga4: {
    title: "Bring in your history (GA4)",
    label: "GA4 history",
    line: "Up to 2 years of pre-launch analytics so your forecasts have depth on day one.",
    why: "Import now so forecasts and trends don't start from an empty month.",
    nextCopy: "bring in your GA4 history so forecasts have depth on day one",
    route: "/Data/Connect",
    cta: "Import history",
    doneCta: "Manage",
    ask: "Help me import my GA4 history"
  },
  events: {
    title: "Track your key actions",
    label: "Key actions",
    line: "Name the actions that count beyond the checkout, like sign-ups, quote or callback requests and store-locator clicks. One call per action. Carts, checkouts and orders already count.",
    why: "Name what counts as a conversion so funnels and lifecycle can see it.",
    nextCopy: "track your key actions",
    route: "/Tracking",
    cta: "Set up events",
    doneCta: "View",
    ask: "Generate my events script"
  },
  products: {
    title: "Your products",
    label: "Products",
    line: "Your catalog powers tracking match, Studio, forecasts and comparables. Feed, CSV/zip or manual.",
    why: "Your catalog links interest to real SKUs and lights up product analytics.",
    nextCopy: "connect your products",
    route: "/Data",
    cta: "Connect",
    doneCta: "Manage"
  },
  customers: {
    title: "Your customers",
    label: "Customers",
    line: "Feed, CSV, or let it build itself: identified visitors become Contacts once the tracker and events are live.",
    why: "Unifies sales, contacts and lifecycle per customer. No clean list required to start.",
    nextCopy: "add your customers",
    route: "/Data",
    cta: "Connect",
    doneCta: "Manage"
  },
  sales: {
    title: "Your sales history",
    label: "Sales history",
    line: "Optional to start, but it joins interest to revenue: top sellers, forecasts, store performance, configuration reporting.",
    why: "This is the payoff step: it turns interest into revenue insight.",
    nextCopy: "connect your sales history to switch on top sellers & forecasts",
    route: "/Data",
    cta: "Connect feed",
    doneCta: "Manage",
    optional: true
  },
  shopify: {
    title: "Connect your Shopify store",
    label: "Shopify store",
    line: "One connection brings in products, orders (as sales analytics) and customers, plus inventory. We show exactly what access is needed before you approve it in Shopify.",
    why: "This is your whole commerce setup in a single step.",
    nextCopy: "connect your Shopify store: products, sales & customers in one step",
    route: "/Shopify",
    doneRoute: "/Manage/Shopify",
    cta: "Connect Shopify",
    doneCta: "Manage store",
    ask: "How do I connect my Shopify store?"
  },
  email: {
    title: "Add what Shopify Email can't",
    label: "Journeys",
    optional: true,
    line: "Shopify Email keeps your welcome, checkout and post-purchase emails. Plunk adds journeys on data Shopify never sees (wholesale reorders, browse follow-ups with recommendations, lifecycle and behavior) as drafts in your brand.",
    why: "The journeys only Plunk can run, ready as drafts. Nothing sends until you turn one on.",
    nextCopy: "add the journeys Shopify Email can't run",
    cta: "See journeys",
    doneCta: "Journeys",
    route: "/Setup/Email",
    doneRoute: "/Email/journeys"
  }
};

// The optional email step for workspaces that are not on Shopify (2026-10-07: they saw the Shopify Email copy).
const EMAIL_META_GENERIC = {
  title: "Set up your email journeys",
  label: "Journeys",
  optional: true,
  line: "Starter journeys are ready as drafts in your brand: Welcome, Cart Recovery, Loyalty and Re-engagement. Review one, set the sender, and switch it on. Nothing sends until you do.",
  why: "Journeys run on your own tracking and sales data, so they reach people at the right moment.",
  nextCopy: "set up your email journeys",
  cta: "See journeys",
  doneCta: "Journeys",
  route: "/Email/journeys",
  doneRoute: "/Email/journeys"
};

// Theme app extension deep link: shopifyThemeEmbedUrl (functions/onboarding-status.js, shared with the Tracking page).
// Opens the theme editor with the Plunk embed pre-toggled; the merchant clicks Save.
function shopifyTrackerMeta(shop) {
  const href = shopifyThemeEmbedUrl(shop);
  return {
    title: "Turn on tracking in your theme",
    label: "Theme embed on & live",
    line: "The Plunk theme embed records every storefront page: visits, product views, scroll, clicks and forms. The app pixel keeps checkout on its own. This step completes when the embed reports its first visit.",
    why: "Required: one click in your theme editor turns the Plunk embed on. Click Save there, then open your storefront once.",
    nextCopy: "turn on the Plunk theme embed in your theme editor",
    href,
    cta: "Turn on theme embed",
    doneCta: "Theme embed",
    route: "/Tracking"
  };
}
class SetupView extends React.Component {
  constructor(props) {
    super(props);
    // "Before you send" (2026-10-07): brand kit address, a verified sender domain and the old unsubscribe list.
    // Each read soft-fails to "not done yet"; none of them blocks the page.
    _defineProperty(this, "unsubKey", () => `plunk-setup-unsubs-skipped:${(account.get() || {}).tenantId || "ws"}`);
    _defineProperty(this, "skipUnsubs", () => {
      try {
        localStorage.setItem(this.unsubKey(), "1");
      } catch (e) {/* private mode */}
      this.setState({
        unsubSkipped: true
      });
    });
    _defineProperty(this, "go", route => this.props.history.push(route));
    _defineProperty(this, "markComplete", async () => {
      markSetupComplete(true);
      const ob = await getOnboardingStatus(true).catch(() => null);
      if (this._alive) this.setState({
        ob
      });
      window.dispatchEvent(new CustomEvent("plunk:onboarding-changed"));
    });
    _defineProperty(this, "reopenSetup", async () => {
      markSetupComplete(false);
      const ob = await getOnboardingStatus(true).catch(() => null);
      if (this._alive) this.setState({
        ob
      });
      window.dispatchEvent(new CustomEvent("plunk:onboarding-changed"));
    });
    this.state = {
      ob: null,
      brand: null,
      senders: null,
      suppressed: null,
      unsubSkipped: false
    };
    this._alive = true;
    const feat = n => {
      try {
        return hasTenantFeature(n);
      } catch (e) {
        return false;
      }
    };
    this._emailOn = feat("emailAutomation");
    this._campaignsOn = feat("campaigns");
    this._inventory = feat("inventory");
    this._quotes = feat("quotes");
    this._territories = feat("territoryRep");
    this._sales = feat("salesTransactions");
    this._stores = feat("storePerformance");
    this._reps = (() => {
      try {
        return sellsThroughReps();
      } catch (e) {
        return true;
      }
    })();
    this._selfManaged = (() => {
      try {
        return adBillingMode() === "selfmanaged";
      } catch (e) {
        return false;
      }
    })();
    this._managed = !this._selfManaged;
    // Cross-link only: Studio keeps its OWN onboarding — we just point tenants who
    // have Studio at it (SSO carries the session across the studio.plunk.io origin).
    // Provisional (login claim); refined from the workspace's LIVE plans on mount — the claim
    // defaults to "both apps" for Shopify-minted logins (user 2026-09-26: only show the card
    // when the workspace actually has Studio).
    this._hasStudio = ((account.get() || {}).apps || []).indexOf("studio") !== -1;
    this._studioUrl = typeof window !== "undefined" && window.location && window.location.hostname === "staging.plunk.io" ? "https://studio.staging.plunk.io" : "https://studio.plunk.io";
  }
  async componentDidMount() {
    // A merchant-first Shopify install signs the user in WHILE the workspace is still being
    // provisioned, so the session can predate the tenant registry row — no feature flags, no
    // Campaigns menu, "managed" copy on Social. Re-read the broker session once here and reload
    // if flags arrived (2026-09-26).
    try {
      if (!(getTenantFeatures() || []).length && !sessionStorage.getItem("plunk.setup.cfgRefreshed")) {
        sessionStorage.setItem("plunk.setup.cfgRefreshed", "1");
        const ok = await Auth.refreshTenantConfig();
        if (ok && (getTenantFeatures() || []).length) {
          window.location.reload();
          return;
        }
      }
    } catch (e) {/* best effort */}
    getLivePlans().then(plans => {
      if (!this._alive || !Array.isArray(plans)) return; // unknown → keep the claim-based default
      const hasStudio = plans.some(k => k.indexOf("studio") === 0);
      if (hasStudio !== this._hasStudio) {
        this._hasStudio = hasStudio;
        this.forceUpdate();
      }
    }).catch(() => {});
    this.loadSendReadiness();
    let ob = await getOnboardingStatus(true).catch(() => null);
    // Preview override: /Setup?shopify=1 forces the Shopify collapse on any tenant
    // (for demoing the branch — real Shopify tenants get it automatically).
    try {
      if (ob && !ob.shopify && /[?&]shopify=1\b/.test(window.location.search)) {
        const order = ["company", "tracker", "events", "shopify"];
        const done = order.filter(k => ob.steps[k]).length;
        ob = {
          ...ob,
          shopify: true,
          order,
          done,
          total: order.length,
          next: order.find(k => !ob.steps[k]) || null
        };
      }
    } catch (e) {/* no override */}
    if (this._alive) this.setState({
      ob
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  loadSendReadiness() {
    try {
      this.setState({
        unsubSkipped: localStorage.getItem(this.unsubKey()) === "1"
      });
    } catch (e) {/* private mode */}
    loadBrandKit(true).then(b => this._alive && this.setState({
      brand: b || {}
    })).catch(() => this._alive && this.setState({
      brand: {}
    }));
    if (!this._emailOn) return;
    emailApi("GET", "/senders").then(d => this._alive && this.setState({
      senders: d && d.items || []
    })).catch(() => this._alive && this.setState({
      senders: []
    }));
    emailApi("GET", "/suppression/stats").then(d => this._alive && this.setState({
      suppressed: Number(d && d.total || 0)
    })).catch(() => this._alive && this.setState({
      suppressed: 0
    }));
  }
  readyCard({
    icon,
    title,
    line,
    done,
    doneLabel,
    pill,
    cta,
    route,
    extra
  }) {
    return /*#__PURE__*/React.createElement("div", {
      className: `ob-card${done ? " done" : ""}`
    }, /*#__PURE__*/React.createElement("div", {
      className: `ob-st${done ? " done" : ""}`
    }, done ? "✓" : icon), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, title), done ? /*#__PURE__*/React.createElement("span", {
      className: "ob-pill ok"
    }, doneLabel || "Done") : pill ? /*#__PURE__*/React.createElement("span", {
      className: "ob-pill warn"
    }, pill) : null), /*#__PURE__*/React.createElement("p", null, line)), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: `ob-btn${done ? " ghost sm" : ""}`,
      onClick: () => this.go(route)
    }, cta), !done && extra));
  }
  renderBeforeYouSend(ob) {
    const {
      brand,
      senders,
      suppressed,
      unsubSkipped
    } = this.state;
    const hasAddress = !!(brand && String(brand.mailingAddress || "").trim());
    const verified = (senders || []).find(x => String(x.domainAuthStatus || "").toLowerCase() === "verified");
    const unsubsDone = unsubSkipped || (suppressed || 0) > 0;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ob-band"
    }, "Before you send \xB7 email, quotes and digests"), this.readyCard({
      icon: "✎",
      title: "Your brand kit",
      route: "/Settings/Brand",
      cta: hasAddress ? "Edit brand kit" : "Open brand kit",
      done: hasAddress,
      pill: this._emailOn ? "Needed to send email" : null,
      line: "Company name, mailing address, logo and brand voice. Every marketing email must carry your postal address, so journeys and campaigns stay off until it is set. Quotes, sales digests and Content Hub drafts use the rest."
    }), this._emailOn && this.readyCard({
      icon: "@",
      title: "Verify your sender domain",
      route: "/Email/senders",
      cta: verified ? "Senders" : "Add a sender",
      done: !!verified,
      doneLabel: verified ? `Sending as ${verified.domain || verified.fromEmail}` : "Done",
      line: "Add the address your email comes from and verify its domain with a few DNS records. Until a domain is verified, journeys send from no-reply@plunk.io with replies to you, and campaigns can't be scheduled."
    }), this._emailOn && !ob.shopify && this.readyCard({
      icon: "⊘",
      title: "Bring over your unsubscribes",
      route: "/Audiences/Suppression",
      cta: unsubsDone ? "Suppression list" : "Import list",
      done: unsubsDone,
      doneLabel: unsubSkipped && !(suppressed > 0) ? "Nothing to import" : `${(suppressed || 0).toLocaleString()} suppressed`,
      line: "Moving from another email tool? Import the people who unsubscribed or bounced there, so Plunk never emails them. A CSV with one email per row is enough.",
      extra: /*#__PURE__*/React.createElement("span", {
        className: "ob-ask-link",
        onClick: this.skipUnsubs
      }, "nothing to import")
    }));
  }
  renderSalesSetup(ob) {
    const go = (route, label, title) => /*#__PURE__*/React.createElement("button", {
      key: route,
      className: "ob-btn ghost sm",
      onClick: () => this.go(route),
      title: title
    }, label);
    // Reps-and-dealers setup only when the business model is not retail AND the workspace is not a Shopify store
    // (2026-11-03, review item 14): a Shopify merchant sells direct whatever the registry says.
    if (this._reps && !ob.shopify && (this._sales || this._quotes || this._territories)) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "ob-band"
      }, "Sales setup \xB7 any order"), /*#__PURE__*/React.createElement("div", {
        className: "ob-card"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-st"
      }, "$"), /*#__PURE__*/React.createElement("div", {
        className: "ob-t"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-h"
      }, /*#__PURE__*/React.createElement("b", null, "Set up selling through reps and dealers")), /*#__PURE__*/React.createElement("p", null, this._quotes ? "Set dealer price tiers before your first quote: without them every quote goes out at list price. " : "", "Plan targets for reps and dealers", this._territories ? ", assign territories by ZIP or state" : "", ", and choose who gets the weekly sales digests (they stay off until you switch them on)."), /*#__PURE__*/React.createElement("div", {
        className: "ob-act",
        style: {
          marginLeft: 0,
          marginTop: 10,
          flexDirection: "row",
          flexWrap: "wrap"
        }
      }, this._quotes && go("/Data/PriceTiers", "Price tiers", "Dealer discounts applied to every quote"), go("/Sales/targets", "Targets"), this._territories && go("/Data/Territories", "Territories"), go("/Data/Digests", "Sales digests")))));
    }
    if (!this._reps && this._stores) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "ob-band"
      }, "Store setup \xB7 any order"), /*#__PURE__*/React.createElement("div", {
        className: "ob-card"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-st"
      }, "$"), /*#__PURE__*/React.createElement("div", {
        className: "ob-t"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-h"
      }, /*#__PURE__*/React.createElement("b", null, "Set store targets and digests")), /*#__PURE__*/React.createElement("p", null, "Plan monthly targets for each store, and choose who gets the weekly store digest (it stays off until you switch it on)."), /*#__PURE__*/React.createElement("div", {
        className: "ob-act",
        style: {
          marginLeft: 0,
          marginTop: 10,
          flexDirection: "row",
          flexWrap: "wrap"
        }
      }, go("/Stores/targets", "Store targets"), go("/Data/Digests", "Store digest")))));
    }
    return null;
  }
  render() {
    const {
      ob
    } = this.state;
    if (!ob) return /*#__PURE__*/React.createElement("div", {
      className: "ob-root"
    }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement(Loader, null));
    const steps = ob.steps || {};
    // Step meta: Shopify stores swap the tracker step for the theme-embed version everywhere
    // (card, "Next:" line, the rail and the Ask Plunk prompt), not only on the card.
    const metaFor = key => key === "tracker" && ob.shopify ? shopifyTrackerMeta(ob.shop) : key === "email" && !ob.shopify ? EMAIL_META_GENERIC : META[key] || {};
    const order = ob.order || [];
    const done = ob.done,
      total = ob.total || order.length;
    const pct = Math.round(done / Math.max(1, total) * 100);
    const acct = account.get() || {};
    const name = acct.account || acct.domain || "your workspace";
    return /*#__PURE__*/React.createElement("div", {
      className: "ob-root"
    }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
      className: "ob-shell"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-eyebrow"
    }, "Set up ", name, ob.shopify ? " · Shopify" : ""), /*#__PURE__*/React.createElement("div", {
      className: "ob-strip"
    }, /*#__PURE__*/React.createElement("b", null, "Workspace setup: ", done, " of ", total), /*#__PURE__*/React.createElement("div", {
      className: "ob-bar"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${pct}%`
      }
    })), /*#__PURE__*/React.createElement("div", {
      className: "ob-chips"
    }, done >= total ? /*#__PURE__*/React.createElement("span", {
      className: "ok"
    }, "Complete \u2713") : /*#__PURE__*/React.createElement("span", null, total - done, " left")), /*#__PURE__*/React.createElement("div", {
      className: "ob-markdone"
    }, ob.marked ? /*#__PURE__*/React.createElement("span", null, "Marked complete \xB7 ", /*#__PURE__*/React.createElement("a", {
      onClick: this.reopenSetup
    }, "Reopen setup")) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ob-btn ghost sm",
      onClick: this.markComplete,
      title: "Close the checklist for this workspace. You can reopen it any time"
    }, "Mark setup complete"))), /*#__PURE__*/React.createElement("div", {
      className: "ob-hrow"
    }, /*#__PURE__*/React.createElement("h2", null, "Connect your data")), /*#__PURE__*/React.createElement("div", {
      className: "ob-next"
    }, ob.next ? /*#__PURE__*/React.createElement(React.Fragment, null, "Next: ", /*#__PURE__*/React.createElement("b", null, metaFor(ob.next).nextCopy || "the next step")) : /*#__PURE__*/React.createElement("b", {
      style: {
        color: "var(--ob-ok)"
      }
    }, "You're all set. Everything's connected. \uD83C\uDF89")), /*#__PURE__*/React.createElement("div", {
      className: "ob-band"
    }, "Core setup \xB7 in order, each unlocks the next"), order.map((key, i) => {
      // Shopify storefronts: the app pixel covers checkout from install; the theme embed
      // is REQUIRED for on-page tracking (user 2026-10-01), so the step completes only
      // once the embed has reported (functions/onboarding-status.js).
      const s = META[key] ? metaFor(key) : null;
      if (!s) return null;
      const n = i + 1;
      const isDone = !!steps[key];
      const isNext = ob.next === key;
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        className: `ob-card${isNext ? " hot" : ""}${isDone ? " done" : ""}`
      }, /*#__PURE__*/React.createElement("div", {
        className: `ob-st${isDone ? " done" : isNext ? " now" : ""}`
      }, isDone ? "✓" : n), /*#__PURE__*/React.createElement("div", {
        className: "ob-t"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-h"
      }, /*#__PURE__*/React.createElement("b", null, n, " \xB7 ", s.title), isDone && /*#__PURE__*/React.createElement("span", {
        className: "ob-pill ok"
      }, "Done"), !isDone && isNext && /*#__PURE__*/React.createElement("span", {
        className: "ob-pill warn"
      }, "Next up"), !isDone && !isNext && s.optional && /*#__PURE__*/React.createElement("span", {
        className: "ob-pill"
      }, "Optional")), /*#__PURE__*/React.createElement("p", null, s.line), isNext && s.why && /*#__PURE__*/React.createElement("div", {
        className: "ob-why"
      }, "\u25B8 ", s.why)), /*#__PURE__*/React.createElement("div", {
        className: "ob-act"
      }, s.href ? /*#__PURE__*/React.createElement("a", {
        className: `ob-btn${isDone ? " ghost sm" : ""}`,
        href: s.href,
        target: "_blank",
        rel: "noreferrer"
      }, isDone ? s.doneCta : s.cta) : /*#__PURE__*/React.createElement("button", {
        className: `ob-btn${isDone ? " ghost sm" : ""}`,
        onClick: () => this.go(isDone && s.doneRoute ? s.doneRoute : s.route)
      }, isDone ? s.doneCta : s.cta), !isDone && s.ask && /*#__PURE__*/React.createElement("span", {
        className: "ob-ask-link",
        onClick: () => askPlunk(s.ask)
      }, "or ask Plunk")));
    }), /*#__PURE__*/React.createElement("div", {
      className: "ob-band"
    }, "Tune your workspace \xB7 any order"), /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, "\u2726"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Sharpen your workspace")), /*#__PURE__*/React.createElement("p", null, "Define markets, map lifecycle fields, set up configuration reporting", this._inventory ? ", connect inventory" : "", ", and seed your apps. All optional, any time."), /*#__PURE__*/React.createElement("div", {
      className: "ob-act",
      style: {
        marginLeft: 0,
        marginTop: 10,
        flexDirection: "row",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Locations")
    }, "Markets"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Lifecycle")
    }, "Lifecycle"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Data/Rules")
    }, "Config reporting"), this._inventory && /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Inventory")
    }, "Inventory"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Apps")
    }, "Seed apps"), ob.shopify && /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Data/Connect"),
      title: "Optional: up to 2 years of GA4 history for deeper forecasts"
    }, "GA4 history"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Data")
    }, "All data settings \u2192")))), this.renderSalesSetup(ob), this.renderBeforeYouSend(ob), /*#__PURE__*/React.createElement("div", {
      className: "ob-band mk"
    }, "Turn on your marketing \xB7 the day-one win"), this._emailOn && (steps.email ?
    /*#__PURE__*/
    // Journeys brought over via Setup → Bring your email over: point at THEM, not the legacy starter automations.
    React.createElement("div", {
      className: "ob-card hot"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st now"
    }, "\u2605"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Activate your journeys"), /*#__PURE__*/React.createElement("span", {
      className: "ob-pill warn"
    }, "Highest leverage")), /*#__PURE__*/React.createElement("p", null, "Your email journeys are ready as drafts: welcome, abandoned checkout, cart recovery, post-purchase and win-back. Open one, check the sender and copy, and activate it. Nothing sends until you do.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn",
      onClick: () => this.go("/Email/journeys")
    }, "Review & activate"))) : /*#__PURE__*/React.createElement("div", {
      className: "ob-card hot"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st now"
    }, "\u2605"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, ob.shopify ? "Journeys Shopify Email can't run" : "Activate your starter emails"), /*#__PURE__*/React.createElement("span", {
      className: "ob-pill warn"
    }, "Highest leverage")), ob.shopify ? /*#__PURE__*/React.createElement("p", null, "Shopify Email keeps sending your basics. Plunk adds the journeys it cannot run: wholesale reorders, browse follow-ups with recommendations, lifecycle and behavior journeys. ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.go("/Setup/Email")
    }, "See what's ready"), ".") : /*#__PURE__*/React.createElement("p", null, "Starter journeys are already built for you: Welcome, Cart Recovery, Loyalty and Re-engagement. Open one, set the sender, and turn it on.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn",
      onClick: () => this.go("/Email/journeys")
    }, "Activate")))), /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, "\u25CE"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Connect your channels")), /*#__PURE__*/React.createElement("p", null, this._managed ? "Your email, social and publishing connections are set up for you by the Plunk team." : "Connect your email provider, social accounts and publishing tools so campaigns and content can go out.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, this._managed ? /*#__PURE__*/React.createElement("span", {
      className: "ob-pill"
    }, "Managed by Plunk") : /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Data/Connect")
    }, "Connect"))), /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, "\u25A6"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Define your markets"), ob.shopify && /*#__PURE__*/React.createElement("span", {
      className: "ob-pill ok"
    }, "From Shopify")), /*#__PURE__*/React.createElement("p", null, ob.shopify ? "Your Shopify Markets are imported automatically (each market and its countries), so campaigns and Market Intelligence already know where you sell. Review them or add local markets by ZIP." : "Group your geography into the places you sell. Markets power the flight wizard and Market Intelligence.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Locations")
    }, "Define markets"))), this._campaignsOn && /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, "\u2708"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Let Autopilot draft your first flights"), /*#__PURE__*/React.createElement("span", {
      className: "ob-pill"
    }, "Drafts only")), /*#__PURE__*/React.createElement("p", null, hasTenantFeature("shopify") ? "Plunk works alongside Shopify Autopilot: it adds Google Search, Performance Max, YouTube and CTV, email journeys, forecasting, Studio creative and cross-source attribution." : "Autopilot plans Google Search, Performance Max, Display, YouTube and CTV, and Meta flights for your markets, with email journeys, forecasting, Studio creative and cross-source attribution behind them.", " Autopilot drafts inside your guardrails; nothing launches until you approve it.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Campaigns/Autopilot")
    }, "Open Autopilot"))), this._campaignsOn && this._selfManaged && /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, "\u27A4"), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Launch your first flight"), /*#__PURE__*/React.createElement("span", {
      className: "ob-pill"
    }, "Self-managed")), /*#__PURE__*/React.createElement("p", null, "Set your ad provider, then the flight wizard turns your markets and audiences into a live campaign.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.go("/Campaigns/Settings")
    }, "Ad settings"))), this._hasStudio && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ob-band"
    }, "Also using Plunk Studio?"), /*#__PURE__*/React.createElement("div", {
      className: "ob-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-st"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons",
      style: {
        fontSize: 15
      }
    }, "palette")), /*#__PURE__*/React.createElement("div", {
      className: "ob-t"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-h"
    }, /*#__PURE__*/React.createElement("b", null, "Finish your Studio setup")), /*#__PURE__*/React.createElement("p", null, "Studio has its own quick setup: connect your product catalog, add swatches, make your first render, and share a board with a client. It picks up right where you left off.")), /*#__PURE__*/React.createElement("div", {
      className: "ob-act"
    }, /*#__PURE__*/React.createElement("a", {
      className: "ob-btn",
      href: this._studioUrl,
      style: {
        textDecoration: "none",
        display: "inline-block"
      }
    }, "Open Plunk Studio \u2192"))))), /*#__PURE__*/React.createElement("aside", {
      className: "ob-aside"
    }, /*#__PURE__*/React.createElement("h3", null, "Your setup"), /*#__PURE__*/React.createElement("div", {
      className: "lead"
    }, done, " of ", total, " core steps \xB7 derived live from your data."), order.map((key, i) => {
      const s = META[key] ? metaFor(key) : null;
      if (!s) return null;
      const isDone = !!steps[key];
      const isNext = ob.next === key;
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        className: `ob-mini${isNext ? " now" : ""}`
      }, /*#__PURE__*/React.createElement("span", {
        className: `d${isDone ? " done" : isNext ? " now" : ""}`
      }, isDone ? "✓" : i + 1), /*#__PURE__*/React.createElement("span", null, s.label, isNext ? ": next up" : ""));
    }), /*#__PURE__*/React.createElement("div", {
      className: "ob-askcard"
    }, /*#__PURE__*/React.createElement("b", null, "\uD83D\uDCAC Ask Plunk"), /*#__PURE__*/React.createElement("p", null, "I can see where you are. Want a hand with what's next?"), /*#__PURE__*/React.createElement("button", {
      className: "ob-q",
      onClick: () => askPlunk("What do I still need to do to finish setup?")
    }, "\u201CWhat do I still need to do?\u201D"), ob.next && /*#__PURE__*/React.createElement("button", {
      className: "ob-q",
      onClick: () => askPlunk(metaFor(ob.next).ask || `Help me with ${metaFor(ob.next).nextCopy || "the next step"}`)
    }, "\u201CHelp me with the next step\u201D"), /*#__PURE__*/React.createElement("button", {
      className: "ob-q",
      onClick: () => askPlunk("Is my tracker actually live?")
    }, "\u201CIs my tracker live?\u201D")), /*#__PURE__*/React.createElement("div", {
      className: "ob-walk"
    }, "Prefer a person? ", /*#__PURE__*/React.createElement("a", {
      href: `mailto:support@plunk.io?subject=${encodeURIComponent("Onboarding walkthrough request")}&body=${encodeURIComponent("We'd like a guided walkthrough of workspace setup.\n\nWorkspace: " + (acct.tenantId || ""))}`
    }, "Request a walkthrough")))));
  }
}
export default withRouter(SetupView);
export const SetupViewComponent = withRouter(SetupView);